import React, { useState, useEffect, useCallback } from 'react';
import Header from '@/components/Header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Package, Mail, Phone, MessageSquare, Car, TrendingDown, ScanSearch, UserX,
  FileDown, ChevronDown, ChevronUp, Loader2, FileText, Handshake, Calendar, Gauge,
} from 'lucide-react';
import { API } from '@/lib/api';
import { httpRequest, HttpError } from '@/lib/http';

function planLabel(p) {
  if (p === 'serenite') return 'Sérénité';
  if (p === 'essentiel') return 'Essentiel';
  return p || '—';
}

function formatBytes(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const v = Number(n);
  if (v < 1024) return `${v} o`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} Ko`;
  return `${(v / (1024 * 1024)).toFixed(1)} Mo`;
}

async function downloadAdminReportMd(analysisId) {
  const res = await fetch(`${API}/admin/analyses/${analysisId}/report.md`, { credentials: 'include' });
  if (!res.ok) {
    let msg = `Erreur ${res.status}`;
    try {
      const j = await res.json();
      if (j.detail) msg = typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail);
    } catch (_) { /* ignore */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `synthese-${String(analysisId).slice(0, 10)}.md`;
  a.rel = 'noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function AdminDetailPanel({ detail, formatCurrency }) {
  if (!detail) return null;
  const { analysis, summary, document: doc, order, user: u } = detail;
  const cd = summary.contract_details || {};
  const sa = summary.savings_analysis || {};

  return (
    <div
      className="mt-4 pt-4 border-t border-white/[0.08] space-y-4 text-sm text-[#f5f5f7]/70"
      data-testid="admin-detail-panel"
    >
      {doc ? (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35 mb-2 flex items-center gap-2">
            <FileText size={12} /> Document source
          </p>
          <p className="text-[#f5f5f7]/85 font-medium">{doc.original_filename || '—'}</p>
          <p className="text-xs text-[#f5f5f7]/45 mt-1">
            {formatBytes(doc.size)}
            {doc.content_type ? ` · ${doc.content_type}` : ''}
            {doc.created_at ? ` · envoyé ${new Date(doc.created_at).toLocaleString('fr-FR')}` : ''}
          </p>
        </div>
      ) : (
        <p className="text-xs text-[#f5f5f7]/40">Aucune métadonnée document liée.</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35 mb-2">Contrat (extrait)</p>
          <ul className="space-y-1 text-xs">
            <li>Type : <span className="text-[#f5f5f7]/80">{summary.document_type || '—'}</span></li>
            <li>Perte financière au contrat :{' '}
              <span className="text-[#f5f5f7]/80">{summary.financial_loss_contract_included ? 'oui' : 'non'}</span>
            </li>
            <li>Mensualité contrat : {formatCurrency(cd.monthly_payment)}</li>
            <li>Durée : {cd.duration_months != null ? `${cd.duration_months} mois` : '—'}</li>
            <li>Mois restants : {cd.remaining_months != null ? cd.remaining_months : '—'}</li>
            <li>Fin contrat : {cd.end_date || cd.lease_end_date || '—'}</li>
            <li>Km / an : {cd.annual_mileage_km != null ? cd.annual_mileage_km : (cd.total_mileage != null ? `${cd.total_mileage} km (total)` : '—')}</li>
            {cd.financial_loss_monthly != null ? (
              <li>Perte fin. (mens.) : {formatCurrency(cd.financial_loss_monthly)}</li>
            ) : null}
          </ul>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35 mb-2">Analyse économique</p>
          <ul className="space-y-1 text-xs">
            <li>Économie totale : <span className="font-semibold savings-amount">{formatCurrency(sa.total_savings)}</span></li>
            <li>Mensualité : {formatCurrency(sa.current_monthly)} → {formatCurrency(sa.proposed_monthly)}</li>
            <li>Économie mensuelle : {formatCurrency(sa.monthly_savings)}</li>
            <li>Ré-analyses : {analysis.reanalyze_count ?? 0}</li>
            <li>Formule (analyse) : {planLabel(analysis.selected_plan)}</li>
            <li>Indice confiance : {summary.confidence_score != null ? summary.confidence_score : '—'}</li>
          </ul>
        </div>
      </div>

      {summary.summary_text ? (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35 mb-1">Résumé IA</p>
          <p className="text-xs leading-relaxed text-[#f5f5f7]/65 whitespace-pre-wrap">{summary.summary_text}</p>
        </div>
      ) : null}

      {summary.recommendations?.length ? (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35 mb-1">Recommandations</p>
          <ul className="list-disc list-inside text-xs space-y-1 text-[#f5f5f7]/60">
            {summary.recommendations.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35 mb-1">Compte client</p>
          {u ? (
            <p className="text-[#f5f5f7]/75">{u.name || '—'} · {u.email}</p>
          ) : (
            <p className="text-[#f5f5f7]/45">Pas encore rattaché (scan anonyme)</p>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35 mb-1">Commande liée</p>
          {order ? (
            <p className="text-[#f5f5f7]/75">
              Statut liste : {order.status} · {formatCurrency(order.commission_amount)} · {planLabel(order.selected_plan)}
              {order.paid_via_stripe ? ' · Stripe' : ''}
            </p>
          ) : (
            <p className="text-[#f5f5f7]/45">Aucune commande (pas encore confirmé / payé)</p>
          )}
        </div>
      </div>
    </div>
  );
}

const QUOTE_STATUS_LABELS = {
  submitted: 'Reçu',
  in_progress: 'En cours',
  answered: 'Répondu',
  abandoned: 'Abandonné',
};

const QUOTE_STATUS_COLORS = {
  submitted: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
  in_progress: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  answered: 'bg-green-500/10 text-green-400 border-green-500/30',
  abandoned: 'bg-[#2c2c2e] text-[#f5f5f7]/40 border-white/10',
};

function QuoteCard({ quote, onStatusChange, onDownload, formatDate, formatCurrency }) {
  const status = quote.status || 'submitted';
  const ticketShort = (quote.id || '').split('-')[0].toUpperCase();
  return (
    <div className="bg-[#1c1c1e] border border-white/8 p-6 rounded-xl" data-testid={`quote-${quote.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-1">
            <h3 className="text-base font-bold font-heading text-[#f5f5f7] truncate">
              {quote.contact_email || '(sans email)'}
            </h3>
            <Badge className={`rounded-lg text-xs ${QUOTE_STATUS_COLORS[status] || ''}`}>
              {QUOTE_STATUS_LABELS[status] || status}
            </Badge>
            <span className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35 font-mono">#{ticketShort}</span>
          </div>
          <p className="text-xs text-[#f5f5f7]/40">{formatDate(quote.created_at)}</p>
        </div>
        <Select value={status} onValueChange={(val) => onStatusChange(quote.id, val)}>
          <SelectTrigger className="w-full sm:w-44 bg-[#2c2c2e] border-white/10 text-[#f5f5f7] h-9 text-xs rounded-lg">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1c1c1e] border-white/10 text-[#f5f5f7]">
            {Object.entries(QUOTE_STATUS_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4 text-sm">
        <div className="flex items-start gap-2">
          <Car size={14} className="text-[#f5f5f7]/40 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35">Véhicule</p>
            <p className="text-[#f5f5f7]/85 truncate">{quote.vehicle || '—'}</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <TrendingDown size={14} className="text-[#f5f5f7]/40 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35">Mensualité devis</p>
            <p className="text-[#f5f5f7]/85">{quote.monthly_quote_eur != null ? formatCurrency(quote.monthly_quote_eur) : '—'}</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Calendar size={14} className="text-[#f5f5f7]/40 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35">Durée</p>
            <p className="text-[#f5f5f7]/85">{quote.duration_months != null ? `${quote.duration_months} mois` : '—'}</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Gauge size={14} className="text-[#f5f5f7]/40 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35">KM/an</p>
            <p className="text-[#f5f5f7]/85">{quote.annual_km != null ? `${quote.annual_km}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : '—'}</p>
          </div>
        </div>
      </div>

      {quote.contact_phone ? (
        <div className="flex items-center gap-2 text-sm text-[#f5f5f7]/55 mb-3">
          <Phone size={14} className="text-[#f5f5f7]/40" />
          <a href={`tel:${quote.contact_phone}`} className="hover:text-[#f5f5f7]">{quote.contact_phone}</a>
        </div>
      ) : null}

      {quote.notes ? (
        <div className="mb-4">
          <p className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35 mb-1 flex items-center gap-1">
            <MessageSquare size={12} /> Notes
          </p>
          <p className="text-sm text-[#f5f5f7]/70 whitespace-pre-wrap">{quote.notes}</p>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-white/[0.06]">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 rounded-lg border-white/[0.12] bg-[#2c2c2e] text-[#f5f5f7] hover:bg-[#3a3a3c] gap-2 text-xs"
          onClick={() => onDownload(quote.id, quote.original_filename)}
          data-testid={`quote-download-${quote.id}`}
        >
          <FileDown size={14} />
          {quote.original_filename || 'Télécharger le devis'}
        </Button>
        {quote.size != null ? (
          <span className="text-xs text-[#f5f5f7]/35">{formatBytes(quote.size)}</span>
        ) : null}
        <a
          href={`mailto:${quote.contact_email}?subject=Re%3A%20Devis%20%23${ticketShort}%20%E2%80%94%20LeasingMalin`}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-[#f5f5f7]/60 hover:text-[#f5f5f7] transition-opacity ml-auto"
        >
          <Mail size={14} />
          Répondre
        </a>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [orders, setOrders] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [detailsByAnalysisId, setDetailsByAnalysisId] = useState({});
  const [loadingDetailId, setLoadingDetailId] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ordersData, analysesData, quotesData] = await Promise.all([
        httpRequest(`${API}/admin/orders`),
        httpRequest(`${API}/admin/analyses?limit=300`),
        httpRequest(`${API}/admin/quotes?limit=200`),
      ]);
      setOrders(ordersData);
      setAnalyses(analysesData);
      setQuotes(quotesData?.quotes || []);
    } catch (err) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const toggleDetail = async (analysisId) => {
    const isOpen = !!expanded[analysisId];
    setExpanded((e) => ({ ...e, [analysisId]: !isOpen }));
    if (isOpen) return;
    if (detailsByAnalysisId[analysisId]) return;
    setLoadingDetailId(analysisId);
    try {
      const d = await httpRequest(`${API}/admin/analyses/${analysisId}/detail`);
      setDetailsByAnalysisId((x) => ({ ...x, [analysisId]: d }));
    } catch {
      toast.error('Impossible de charger le détail du dossier');
      setExpanded((e) => ({ ...e, [analysisId]: false }));
    } finally {
      setLoadingDetailId(null);
    }
  };

  const onDownloadMd = async (analysisId) => {
    try {
      await downloadAdminReportMd(analysisId);
      toast.success('Synthèse .md téléchargée');
    } catch (e) {
      toast.error(e.message || 'Synthèse indisponible');
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await httpRequest(`${API}/admin/orders/${orderId}/status`, {
        method: 'PATCH',
        body: { status },
      });
      toast.success(`Statut mis a jour: ${status}`);
      loadAll();
    } catch (err) {
      toast.error(err instanceof HttpError ? err.message : "Erreur");
    }
  };

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await httpRequest(`${API}/admin/quotes/${quoteId}/status`, {
        method: 'PATCH',
        body: { status },
      });
      toast.success(`Statut : ${QUOTE_STATUS_LABELS[status] || status}`);
      // Mise à jour locale (évite recharger toute la liste)
      setQuotes((prev) => prev.map((q) => (q.id === quoteId ? { ...q, status } : q)));
    } catch (err) {
      toast.error(err instanceof HttpError ? err.message : "Erreur");
    }
  };

  const downloadQuoteFile = async (quoteId, filename) => {
    try {
      const res = await fetch(`${API}/admin/quotes/${quoteId}/file`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `devis-${quoteId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || 'Téléchargement impossible');
    }
  };

  const formatCurrency = (val) => {
    if (val == null) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  const statusColors = {
    new: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
    in_progress: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    completed: 'bg-green-500/10 text-green-400 border-green-500/30',
    cancelled: 'bg-red-950/40 text-red-400 border-red-500/30',
  };

  const statusLabels = { new: 'Nouvelle', in_progress: 'En cours', completed: 'Terminee', cancelled: 'Annulee' };

  const paymentLabels = {
    none: 'Scan seul',
    pending: 'En attente paiement',
    accepted: 'Acceptee',
    in_progress: 'En cours',
    completed: 'Terminee',
    cancelled: 'Annulee',
  };

  const paymentBadgeClass = (ps) => {
    if (ps === 'none') return 'bg-[#2c2c2e] text-[#f5f5f7]/50 border-white/10';
    if (ps === 'pending') return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30';
    if (ps === 'accepted' || ps === 'in_progress') return 'bg-green-500/10 text-green-400 border-green-500/30';
    return 'bg-[#2c2c2e] text-[#f5f5f7]/60 border-white/10';
  };

  const dossierActions = (analysisId) => (
    <div className="flex flex-wrap items-center gap-2 mt-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9 rounded-lg border-white/[0.12] bg-[#2c2c2e] text-[#f5f5f7] hover:bg-[#3a3a3c] gap-2 text-xs"
        onClick={() => onDownloadMd(analysisId)}
        data-testid={`admin-download-md-${analysisId}`}
      >
        <FileDown size={14} />
        Télécharger la synthèse (.md)
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9 rounded-lg border-white/[0.12] bg-[#2c2c2e] text-[#f5f5f7] hover:bg-[#3a3a3c] gap-2 text-xs"
        onClick={() => toggleDetail(analysisId)}
        data-testid={`admin-toggle-detail-${analysisId}`}
      >
        {loadingDetailId === analysisId ? (
          <Loader2 size={14} className="animate-spin" />
        ) : expanded[analysisId] ? (
          <ChevronUp size={14} />
        ) : (
          <ChevronDown size={14} />
        )}
        {expanded[analysisId] ? 'Masquer le détail' : 'Voir le détail du dossier'}
      </Button>
    </div>
  );

  // Mode segmenté : 'eco' (optimisation contrat existant) vs 'nego' (négociation devis avant signature).
  // Persiste dans l'URL via ?mode=nego pour bookmarker / partager.
  const [mode, setMode] = useState(() => {
    if (typeof window === 'undefined') return 'eco';
    const m = new URLSearchParams(window.location.search).get('mode');
    return m === 'nego' ? 'nego' : 'eco';
  });

  const switchMode = (next) => {
    setMode(next);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (next === 'eco') url.searchParams.delete('mode'); else url.searchParams.set('mode', next);
      window.history.replaceState({}, '', url.toString());
    }
  };

  const quoteCounts = quotes.reduce(
    (acc, q) => {
      const s = q.status || 'submitted';
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    {},
  );

  return (
    <div className="min-h-screen bg-black" data-testid="admin-page">
      <Header />
      <main className="pt-24 pb-16 max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#f5f5f7] font-heading">
            Gestion
          </h1>
          <p className="text-base text-[#f5f5f7]/50 mt-1">
            {mode === 'eco'
              ? `${orders.length} commande${orders.length !== 1 ? 's' : ''} confirmée${orders.length !== 1 ? 's' : ''} · ${analyses.length} analyse${analyses.length !== 1 ? 's' : ''} récente${analyses.length !== 1 ? 's' : ''}`
              : `${quotes.length} demande${quotes.length !== 1 ? 's' : ''} de négociation`}
          </p>
        </div>

        {/* Segmented control mode */}
        <div className="mb-8 inline-flex w-full sm:w-auto rounded-xl border border-white/[0.08] bg-[#1c1c1e] p-1 text-sm font-semibold">
          <button
            type="button"
            onClick={() => switchMode('eco')}
            data-testid="admin-mode-eco"
            className={`flex-1 sm:flex-none px-5 py-2.5 rounded-lg transition-colors duration-[180ms] flex items-center justify-center gap-2 ${
              mode === 'eco' ? 'bg-[#2c2c2e] text-[#f5f5f7]' : 'text-[#f5f5f7]/55 hover:text-[#f5f5f7]/85'
            }`}
          >
            <TrendingDown size={15} />
            Optimisation contrat
            <span className="text-xs font-normal text-[#f5f5f7]/40 ml-1">({orders.length + analyses.length})</span>
          </button>
          <button
            type="button"
            onClick={() => switchMode('nego')}
            data-testid="admin-mode-nego"
            className={`flex-1 sm:flex-none px-5 py-2.5 rounded-lg transition-colors duration-[180ms] flex items-center justify-center gap-2 ${
              mode === 'nego' ? 'bg-[#2c2c2e] text-[#f5f5f7]' : 'text-[#f5f5f7]/55 hover:text-[#f5f5f7]/85'
            }`}
          >
            <Handshake size={15} />
            Négociation devis
            <span className="text-xs font-normal text-[#f5f5f7]/40 ml-1">({quotes.length})</span>
          </button>
        </div>

        {/* Mode-specific intro */}
        {mode === 'eco' ? (
          <div className="mb-8 rounded-xl border border-white/[0.08] bg-[#1c1c1e]/80 px-4 py-3 text-sm text-[#f5f5f7]/55">
            <strong className="text-[#f5f5f7]/80">Optimisation de contrat existant.</strong>{' '}
            «&nbsp;Commandes&nbsp;» = dossiers après inscription, choix de formule et confirmation.
            «&nbsp;Analyses&nbsp;» = tous les scans (inclut les sans-suite). Utilisez{' '}
            <strong className="text-[#f5f5f7]/70">Voir le détail</strong> et{' '}
            <strong className="text-[#f5f5f7]/70">Télécharger la synthèse</strong> par dossier.
          </div>
        ) : (
          <div className="mb-8 rounded-xl border border-green-500/20 bg-green-950/20 px-4 py-3 text-sm text-[#f5f5f7]/65">
            <strong className="text-green-300">Négociation avant signature.</strong>{' '}
            Devis envoyés via <code className="text-green-300/90">/negociation</code>. Réponds par e-mail
            sous 24&nbsp;h, télécharge le PDF/photo joint, et fais évoluer le statut au fil des échanges.
          </div>
        )}

        {/* Mode-specific KPIs */}
        {mode === 'eco' ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#1c1c1e] border border-white/8 p-5 rounded-xl">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-yellow-400 mb-1">Nouvelles</p>
              <p className="text-2xl font-black font-heading text-[#f5f5f7]">{orders.filter(o => o.status === 'new').length}</p>
            </div>
            <div className="bg-[#1c1c1e] border border-white/8 p-5 rounded-xl">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-blue-400 mb-1">En cours</p>
              <p className="text-2xl font-black font-heading text-[#f5f5f7]">{orders.filter(o => o.status === 'in_progress').length}</p>
            </div>
            <div className="bg-[#1c1c1e] border border-white/8 p-5 rounded-xl">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-green-400 mb-1">Terminees</p>
              <p className="text-2xl font-black font-heading text-[#f5f5f7]">{orders.filter(o => o.status === 'completed').length}</p>
            </div>
            <div className="bg-[#1c1c1e] border border-white/8 p-5 rounded-xl">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#f5f5f7]/40 mb-1">Commission totale</p>
              <p className="text-2xl font-black commission-amount">{formatCurrency(orders.reduce((s, o) => s + (o.commission_amount || 0), 0))}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#1c1c1e] border border-white/8 p-5 rounded-xl">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-yellow-400 mb-1">Reçus</p>
              <p className="text-2xl font-black font-heading text-[#f5f5f7]">{quoteCounts.submitted || 0}</p>
            </div>
            <div className="bg-[#1c1c1e] border border-white/8 p-5 rounded-xl">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-blue-400 mb-1">En cours</p>
              <p className="text-2xl font-black font-heading text-[#f5f5f7]">{quoteCounts.in_progress || 0}</p>
            </div>
            <div className="bg-[#1c1c1e] border border-white/8 p-5 rounded-xl">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-green-400 mb-1">Répondus</p>
              <p className="text-2xl font-black font-heading text-[#f5f5f7]">{quoteCounts.answered || 0}</p>
            </div>
            <div className="bg-[#1c1c1e] border border-white/8 p-5 rounded-xl">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#f5f5f7]/40 mb-1">Abandonnés</p>
              <p className="text-2xl font-black font-heading text-[#f5f5f7]/60">{quoteCounts.abandoned || 0}</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="spinner" /></div>
        ) : mode === 'nego' ? (
          // Mode négociation : liste plate, pas de tabs (un seul type de données).
          quotes.length === 0 ? (
            <div className="bg-[#1c1c1e] border border-white/8 p-16 text-center rounded-xl" data-testid="quotes-empty">
              <Handshake size={48} className="mx-auto text-[#f5f5f7]/30 mb-4" />
              <p className="text-base text-[#f5f5f7]/50">Aucune demande de négociation pour le moment.</p>
              <p className="text-sm text-[#f5f5f7]/35 mt-2">
                Les soumissions depuis <code className="text-[#f5f5f7]/60">/negociation</code> apparaîtront ici.
              </p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="quotes-list">
              {quotes.map((q) => (
                <QuoteCard
                  key={q.id}
                  quote={q}
                  onStatusChange={updateQuoteStatus}
                  onDownload={downloadQuoteFile}
                  formatDate={formatDate}
                  formatCurrency={formatCurrency}
                />
              ))}
            </div>
          )
        ) : (
          <Tabs defaultValue="orders" className="w-full">
            <TabsList className="mb-6 h-auto flex-wrap gap-1 rounded-xl border border-white/[0.08] bg-[#1c1c1e] p-1 text-[#f5f5f7]/60">
              <TabsTrigger
                value="orders"
                className="rounded-lg data-[state=active]:bg-[#2c2c2e] data-[state=active]:text-[#f5f5f7]"
              >
                Commandes ({orders.length})
              </TabsTrigger>
              <TabsTrigger
                value="analyses"
                className="rounded-lg data-[state=active]:bg-[#2c2c2e] data-[state=active]:text-[#f5f5f7]"
              >
                Analyses / scans ({analyses.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="orders" className="mt-0">
              {orders.length === 0 ? (
                <div className="bg-[#1c1c1e] border border-white/8 p-16 text-center rounded-xl">
                  <Package size={48} className="mx-auto text-[#f5f5f7]/30 mb-4" />
                  <p className="text-base text-[#f5f5f7]/50">Aucune commande confirmée pour le moment</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => {
                    const vehicle = order.result_summary?.vehicle || {};
                    const savings = order.result_summary?.savings || {};
                    const aid = order.analysis_id;
                    return (
                      <div key={order.id} className="bg-[#1c1c1e] border border-white/8 p-6 rounded-xl" data-testid={`order-${order.id}`}>
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                          <div>
                            <div className="flex flex-wrap items-center gap-3 mb-1">
                              <h3 className="text-base font-bold font-heading text-[#f5f5f7]">
                                {order.client_name || order.client_email}
                              </h3>
                              <Badge className={`rounded-lg text-xs ${statusColors[order.status] || ''}`}>
                                {statusLabels[order.status] || order.status}
                              </Badge>
                            </div>
                            <p className="text-xs text-[#f5f5f7]/40">{formatDate(order.created_at)}</p>
                            <div className="flex flex-wrap gap-2 mt-2 text-[11px] text-[#f5f5f7]/50">
                              <span>Formule : <strong className="text-[#f5f5f7]/70">{planLabel(order.selected_plan)}</strong></span>
                              {order.paid_via_stripe ? <Badge className="text-[10px] rounded-md bg-blue-500/15 text-blue-300 border-blue-500/25">Payé via Stripe</Badge> : null}
                              {order.promo_code ? <span>Promo : <strong className="text-green-400/90">{order.promo_code}</strong></span> : null}
                            </div>
                            {aid ? (
                              <p className="font-mono text-[10px] text-[#f5f5f7]/35 mt-2 break-all">Analyse : {aid}</p>
                            ) : null}
                          </div>
                          <Select value={order.status} onValueChange={(val) => updateStatus(order.id, val)}>
                            <SelectTrigger className="w-full sm:w-[160px] h-9 rounded-lg text-xs bg-[#2c2c2e] border-white/8 text-[#f5f5f7]" data-testid={`status-select-${order.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#2c2c2e] border-white/8 text-[#f5f5f7]">
                              <SelectItem value="new">Nouvelle</SelectItem>
                              <SelectItem value="in_progress">En cours</SelectItem>
                              <SelectItem value="completed">Terminee</SelectItem>
                              <SelectItem value="cancelled">Annulee</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-xs uppercase tracking-[0.1em] font-semibold text-[#f5f5f7]/40 mb-2">Contact</p>
                            <div className="space-y-1 text-[#f5f5f7]/60">
                              <p className="flex items-center gap-2"><Mail size={12} /> {order.client_email}</p>
                              {order.client_phone && <p className="flex items-center gap-2"><Phone size={12} /> {order.client_phone}</p>}
                              {order.client_message && <p className="flex items-center gap-2"><MessageSquare size={12} /> {order.client_message}</p>}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-[0.1em] font-semibold text-[#f5f5f7]/40 mb-2">Vehicule</p>
                            <div className="space-y-1 text-[#f5f5f7]/60">
                              <p className="flex items-center gap-2"><Car size={12} /> {vehicle.make || '-'} {vehicle.model || ''}</p>
                              <p>Type: {order.result_summary?.document_type || '-'}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-[0.1em] font-semibold text-[#f5f5f7]/40 mb-2">Financier</p>
                            <div className="space-y-1">
                              <p className="text-[#f5f5f7]/60"><TrendingDown size={12} className="inline mr-1" /> Economie: <span className="font-bold savings-amount">{formatCurrency(savings.total_savings)}</span></p>
                              <p className="text-[#f5f5f7]/60">Commission: <span className="font-bold commission-amount">{formatCurrency(order.commission_amount)}</span></p>
                              <p className="text-[#f5f5f7]/60">Mensualite: {formatCurrency(savings.current_monthly)} → {formatCurrency(savings.proposed_monthly)}</p>
                            </div>
                          </div>
                        </div>

                        {aid ? (
                          <>
                            {dossierActions(aid)}
                            {expanded[aid] ? (
                              loadingDetailId === aid && !detailsByAnalysisId[aid] ? (
                                <div className="flex items-center gap-2 mt-4 text-xs text-[#f5f5f7]/45">
                                  <Loader2 size={14} className="animate-spin" /> Chargement…
                                </div>
                              ) : (
                                <AdminDetailPanel detail={detailsByAnalysisId[aid]} formatCurrency={formatCurrency} />
                              )
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="analyses" className="mt-0">
              {analyses.length === 0 ? (
                <div className="bg-[#1c1c1e] border border-white/8 p-16 text-center rounded-xl">
                  <ScanSearch size={48} className="mx-auto text-[#f5f5f7]/30 mb-4" />
                  <p className="text-base text-[#f5f5f7]/50">Aucune analyse en base</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {analyses.map((row) => (
                    <div
                      key={row.id}
                      className="bg-[#1c1c1e] border border-white/8 p-5 rounded-xl text-sm"
                      data-testid={`admin-analysis-${row.id}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-xs text-[#f5f5f7]/45">{formatDate(row.created_at)}</span>
                          {row.anonymous ? (
                            <Badge className="rounded-lg text-[10px] border border-white/10 bg-[#2c2c2e] text-[#f5f5f7]/50 gap-1">
                              <UserX size={10} /> Anonyme (pas encore de compte)
                            </Badge>
                          ) : (
                            <Badge className="rounded-lg text-[10px] border border-green-500/20 bg-green-500/10 text-green-400 gap-1">
                              <Mail size={10} /> {row.user_email || row.user_id}
                            </Badge>
                          )}
                          <Badge className={`rounded-lg text-[10px] border ${paymentBadgeClass(row.payment_status)}`}>
                            {paymentLabels[row.payment_status] || row.payment_status}
                          </Badge>
                          {row.has_savings ? (
                            <Badge className="rounded-lg text-[10px] border border-green-500/30 bg-green-500/10 text-green-400">
                              Économie détectée
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[#f5f5f7]/65">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35 mb-1">Véhicule</p>
                          <p className="flex items-center gap-2">
                            <Car size={14} className="text-[#f5f5f7]/40 shrink-0" />
                            {[row.vehicle_make, row.vehicle_model].filter(Boolean).join(' ') || '—'}
                          </p>
                          <p className="text-xs text-[#f5f5f7]/40 mt-1">Type : {row.document_type || '—'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35 mb-1">Économie (estim.)</p>
                          <p className="flex items-center gap-2">
                            <TrendingDown size={14} className="text-green-400/80 shrink-0" />
                            <span className="font-bold savings-amount">{formatCurrency(row.total_savings)}</span>
                          </p>
                          <p className="text-xs text-[#f5f5f7]/40 mt-1">
                            Mensualité : {formatCurrency(row.current_monthly)} → {formatCurrency(row.proposed_monthly)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-[#f5f5f7]/35 mb-1">Réf. technique</p>
                          <p className="font-mono text-xs text-[#f5f5f7]/50 break-all">id {row.id}</p>
                          {row.analysis_token ? (
                            <p className="font-mono text-[11px] text-[#f5f5f7]/40 break-all mt-1" title="Token lien public">
                              token {row.analysis_token.slice(0, 12)}…
                            </p>
                          ) : null}
                        </div>
                      </div>
                      {dossierActions(row.id)}
                      {expanded[row.id] ? (
                        loadingDetailId === row.id && !detailsByAnalysisId[row.id] ? (
                          <div className="flex items-center gap-2 mt-4 text-xs text-[#f5f5f7]/45">
                            <Loader2 size={14} className="animate-spin" /> Chargement…
                          </div>
                        ) : (
                          <AdminDetailPanel detail={detailsByAnalysisId[row.id]} formatCurrency={formatCurrency} />
                        )
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
